import { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { logError } from "../lib/log";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export const ZOOM_STEPS = [0.5, 0.65, 0.8, 1, 1.25, 1.5, 1.75, 2, 2.5, 3];

async function renderPdfToContainer(pdf, container, zoom, cancelledRef) {
  while (container.firstChild) container.removeChild(container.firstChild);
  const dpr = window.devicePixelRatio || 1;
  const fitWidth = Math.min(container.clientWidth - 32, 1100);
  const targetWidth = fitWidth * zoom;
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    if (cancelledRef.cancelled) return;
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: (targetWidth / base.width) * dpr });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${viewport.width / dpr}px`;
    canvas.style.height = `${viewport.height / dpr}px`;
    canvas.dataset.page = i;
    canvas.className = "mx-auto mb-4 shadow-lg rounded bg-white";
    container.appendChild(canvas);
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
  }
}

// Owns pdf.js document loading and re-rendering on zoom change.
export function usePdfRenderer(data) {
  const containerRef = useRef(null);
  const pdfRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [numPages, setNumPages] = useState(0);
  const [zoomIdx, setZoomIdx] = useState(3); // index into ZOOM_STEPS (1.0 = "fit")
  const zoomIdxRef = useRef(3);
  zoomIdxRef.current = zoomIdx;

  const renderPages = useCallback(async (cancelledRef) => {
    const container = containerRef.current;
    const pdf = pdfRef.current;
    if (!container || !pdf) return;
    try {
      await renderPdfToContainer(pdf, container, ZOOM_STEPS[zoomIdxRef.current], cancelledRef);
      if (!cancelledRef.cancelled) setStatus("done");
    } catch (e) {
      logError("PDF render failed:", e);
      if (!cancelledRef.cancelled) setStatus("error");
    }
  }, []);

  // Load the document once
  useEffect(() => {
    const ref = { cancelled: false };
    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument({ data: data.slice(0) }).promise;
        if (ref.cancelled) return;
        pdfRef.current = pdf;
        setNumPages(pdf.numPages);
        await renderPages(ref);
      } catch (e) {
        logError("PDF load failed:", e);
        if (!ref.cancelled) setStatus("error");
      }
    })();
    return () => { ref.cancelled = true; };
  }, [data, renderPages]);

  // Re-render on zoom change (document already loaded)
  useEffect(() => {
    if (!pdfRef.current) return;
    const ref = { cancelled: false };
    setStatus("loading");
    renderPages(ref);
    return () => { ref.cancelled = true; };
  }, [zoomIdx, renderPages]);

  return { containerRef, status, numPages, zoomIdx, setZoomIdx };
}
